import { createFileRoute } from "@tanstack/react-router";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";
import { AdministratorsOnly } from "../components/ui/states";

/**
 * The workspace's general settings, read-only: a workspace is its organisation's at Optymyse (owner
 * decision 12), which is where its name and address, its owners and admins, and closing it are managed.
 */
function OrgSettingsPage() {
  const slug = workspaceSlug();
  const view = useWorkspace(slug);

  if (view.isLoading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mx-auto max-w-3xl space-y-3 py-12">
          <div className="h-8 w-48 animate-pulse rounded bg-[hsl(var(--muted))]" />
          <div className="h-40 animate-pulse rounded bg-[hsl(var(--muted))]" />
        </div>
      </div>
    );
  }
  if (!view.data || view.error instanceof ApiError) {
    return <ToWorkspaces />;
  }
  if (view.data.role !== "administrator") {
    return <AdministratorsOnly what={strings.states.adminPages.settings} />;
  }

  const { workspace } = view.data;
  const rows: [string, string][] = [
    [strings.workspaces.settings.nameLabel, workspace.name],
    [strings.workspaces.settings.slugLabel, workspace.slug],
    [strings.workspaces.settings.regionLabel, strings.workspaces.settings.regions[workspace.region]],
  ];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mx-auto max-w-3xl space-y-6 py-12">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">{workspace.name}</h1>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{strings.workspaces.settings.generalTitle}</CardTitle>
            <CardDescription>{strings.workspaces.settings.managedAtOptymyse}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-[hsl(var(--border))] rounded-md border border-[hsl(var(--border))]">
              {rows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-4 p-3 text-sm">
                  <dt className="text-[hsl(var(--muted-foreground))]">{label}</dt>
                  <dd className="font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/admin/settings/")({ component: OrgSettingsPage });
